import { useMemo } from 'react';
import {
  Camera,
  CameraSource,
  CameraResultType,
  Photo,
  GalleryPhoto,
} from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Capacitor } from '@capacitor/core';
import useIonicToast from './useIonicToast';
import { FileTypeKey, FileTypeValue } from '../types/FileType';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

export default function useNativeFilePicker({
  maxFiles = 1,
  ...props
}: {
  maxFiles?: number;
  onChange: (acceptedFiles: File[]) => void;
  acceptableType: Partial<Record<FileTypeKey, Array<FileTypeValue>>>;
}) {
  const { presentIonToast } = useIonicToast();
  const { isNative } = useSelector((state: RootState) => state.settings);
  const AcceptableFileTypes = Object.keys(
    props.acceptableType
  ) as Array<FileTypeKey>;
  const useFileSystem = useMemo(() => {
    return AcceptableFileTypes.some(
      (type) => !type.startsWith('image/') || !type.startsWith('image/')
    );
  }, []);

  const transformToFile = async (file: any) => {
    if (file.path) {
      const url = Capacitor.convertFileSrc(file.path);
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], `${blob.name || Date.now()}`, {
        type: blob.type,
      });
    }
  };

  async function imagePickerMechanism(): Promise<File[]> {
    const result: File[] = [];
    let photos: Array<Photo | GalleryPhoto> = [];
    if (maxFiles <= 1) {
      const newPhoto = await Camera.getPhoto({
        quality: 100,
        promptLabelCancel: 'Cancel',
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
        presentationStyle: 'fullscreen',
      });
      photos.push(newPhoto as Photo);
    } else {
      const selectedImgs = await Camera.pickImages({
        quality: 100,
        limit: maxFiles,
        presentationStyle: 'fullscreen',
      });

      photos = selectedImgs.photos;
    }

    for (const photo of photos as any) {
      const newfile = await transformToFile(photo);
      if (newfile) result.push(newfile);
    }

    return result;
  }

  const filePickerMechanism = async () => {
    const result: File[] = [];
    const selectedFiles = await FilePicker.pickFiles({
      limit: maxFiles,
      types: AcceptableFileTypes,
      readData: true,
    });

    for (const selection of selectedFiles.files) {
      const newFile = await transformToFile(selection);
      if (newFile) result.push(newFile);
    }
    return result;
  };

  async function useFilePicker() {
    try {
      let files: Array<File> = [];

      if (useFileSystem) files = await filePickerMechanism();
      else files = await imagePickerMechanism();

      if (files.length > 0) props.onChange(files);
    } catch (error: any) {
      if (error?.message?.includes('User cancelled')) return;
      presentIonToast(
        error.message || 'Something went wrong, try again!',
        'error'
      );
    }
  }

  return { useFilePicker, isNative };
}
